import { Notifier } from '@airbrake/browser';
import { detect } from 'detect-browser';

let airbrakeInstance = null;

const EXTENSION_URL = /^(chrome-extension|moz-extension|safari-extension|about):/;

// Returns true if a browser-side notice should be dropped. The SDK runs on
// partner library sites, so the dominant noise sources are host-page issues
// rather than bugs in our code:
//   - "Script error." with no stack frames: cross-origin host scripts (the
//     browser strips the message and stack for security).
//   - "ResizeObserver loop limit exceeded": a benign browser-engine quirk.
//   - "Non-Error promise rejection captured": unhandled rejections without
//     an Error wrapper; usually host-page glue code.
//   - Notices whose entire backtrace lives in a browser extension.
export const isBrowserNoiseNotice = notice => {
  const firstError = notice?.errors?.[0];
  const message = firstError?.message || '';
  const backtrace = firstError?.backtrace || [];

  if (message === 'Script error.' && backtrace.length === 0) return true;
  if (/ResizeObserver loop/.test(message)) return true;
  if (/Non-Error promise rejection captured/.test(message)) return true;

  if (backtrace.length > 0 && backtrace.every(frame => EXTENSION_URL.test(frame?.file || ''))) {
    return true;
  }

  return false;
};

const SEVERITY_LEVEL = {
  DEBUG: 'debug',
  ERROR: 'error',
  INFO: 'info'
};

const makeLoggerWithSeverity = severity => {
  return (err, annotations = {}) => {
    if (!airbrakeInstance) {
      throw new Error('Airbrake has not been initialized');
    }

    return airbrakeInstance
      .notify({
        error: err,
        context: { severity },
        ...annotations
      })
      .then(notice => {
        const { error } = notice;

        if (error) {
          /* eslint-disable no-console */
          console.warn('%cFailed to send log to Airbrake', 'color:yellow');
          console.error(error);
          /* eslint-enable no-console */
        }

        return notice;
      });
  };
};

const logger = {
  debug: makeLoggerWithSeverity(SEVERITY_LEVEL.DEBUG),
  error: makeLoggerWithSeverity(SEVERITY_LEVEL.ERROR),
  info: makeLoggerWithSeverity(SEVERITY_LEVEL.INFO)
};

const initialize = ({ appVersion, environment, isDebug = false, isSdk, projectId, projectKey }) => {
  if (!(appVersion && projectId && projectKey)) {
    throw new Error('Airbrake requires appVersion, projectId, and projectKey');
  }

  if (isDebug) {
    /* eslint-disable-next-line no-console */
    console.warn('%cAirbrake logger is in debug mode', 'color:yellow');
  }

  const instrumentation = {
    console: false,
    fetch: false,
    history: false,
    onerror: false,
    unhandledrejection: false,
    xhr: false
  };

  const airbrakeConfig = {
    environment,
    instrumentation,
    projectId,
    projectKey
  };

  airbrakeInstance = new Notifier(airbrakeConfig);

  const browser = detect();
  const version = parseInt(browser?.version || '0', 10);
  const name = browser?.name;
  const unsupportedBrowsers = ['safari', 'ie', 'ios'];

  if (unsupportedBrowsers.includes(name) && version < 11) {
    airbrakeInstance.addFilter(() => null); // nullify notices for old browsers
  }

  // add environment and appVersion to context
  airbrakeInstance.addFilter(notice => {
    notice.context.environment = environment;
    notice.context.isSdk = isSdk;
    notice.context.version = appVersion;

    return notice;
  });

  // prevent pushing notices to Airbrake API when debugging
  airbrakeInstance.addFilter(notice => {
    if (!isDebug) {
      return notice;
    }

    /* eslint-disable no-console */
    console.group('%cAirbrake logger is in debug mode, logging notice to console:', 'color:yellow');
    console.log(notice);
    console.groupEnd();
    /* eslint-enable no-console */

    return null;
  });

  // Drop host-page / browser-quirk notices that historically dominated
  // the SDK's Airbrake volume. See isBrowserNoiseNotice for the rules.
  airbrakeInstance.addFilter(notice => (isBrowserNoiseNotice(notice) ? null : notice));

  // Drop notices from bots — they execute page JS without a real user session
  // and produce noise that masks genuine user-facing errors.
  airbrakeInstance.addFilter(notice => {
    if (/bot|crawler|spider/i.test(notice.context.userAgent)) {
      return null;
    }
    return notice;
  });

  return logger;
};

const getInstance = () => {
  if (!airbrakeInstance) {
    throw new Error('Airbrake has not been initialized');
  }

  return logger;
};

export { initialize, getInstance };
